"use client";

import i18n from "@/lib/i18n"; // Import i18n instance

export const getTodayAtMidnight = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/** Start of insurance can never be today or earlier — the earliest selectable/default date is
 * always tomorrow, per the business spec. */
export const getTomorrowAtMidnight = () => {
  const tomorrow = getTodayAtMidnight();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
};

/**
 * The new Pet V2 funnel (SIP-4337) goes live before the product itself can be sold — policies
 * may only start on or after 1 December 2026, so no start of insurance before that date must be
 * offered. Local midnight, matching how all other start-of-insurance dates are normalised.
 * Once "tomorrow" is past this date the rule neutralises itself (see getEarliestStartOfInsurance).
 */
export const EARLIEST_START_OF_INSURANCE = new Date(2026, 11, 1);

/** Earliest selectable/default start of insurance: tomorrow, but never before
 * EARLIEST_START_OF_INSURANCE. Returns a fresh Date each call. */
export const getEarliestStartOfInsurance = () => {
  const tomorrow = getTomorrowAtMidnight();
  return tomorrow > EARLIEST_START_OF_INSURANCE ? tomorrow : new Date(EARLIEST_START_OF_INSURANCE);
};

/**
 * Display metadata for a bundle tier. Actual coverage detail for a tier is no longer
 * static — it lives in the live `/recommendations` response for that tier
 * (see `FunnelState.bundleRecommendations` / `selectedRecommendation`).
 */
export interface ProductVariant {
  id: "s" | "m" | "l";
  name: string;
  description: string;
}

const NAME_KEYS: Record<"s" | "m" | "l", { nameKey: string; descriptionKey: string }> = {
  s: { nameKey: "priceCustomization.planNames.basic", descriptionKey: "priceCustomization.planNames.basicSubtitle" },
  m: { nameKey: "priceCustomization.planNames.standard", descriptionKey: "priceCustomization.planNames.standardSubtitle" },
  l: { nameKey: "priceCustomization.planNames.premium", descriptionKey: "priceCustomization.planNames.premiumSubtitle" },
};

export const createProductVariant = (bundleId: "s" | "m" | "l"): ProductVariant => {
  const { nameKey, descriptionKey } = NAME_KEYS[bundleId];
  return {
    id: bundleId,
    name: i18n.t(nameKey),
    description: i18n.t(descriptionKey),
  };
};